import { useState, useEffect, useContext } from "react";
import { X } from "lucide-react";
import { useContactGroups } from "../../../context/ContactGroupsContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GroupSelectorModal from "./GroupSelectorModal";
import ContactSelectorModal from "./ContactSelectorModal";
import { CustomerContactsContext } from "../../../context/CustomerContactsContext";

export default function CreateCampaignModal({
  onClose,
  campaignType,
  refetchCampaigns,
}) {
  const { groups, fetchGroups, listGroupContacts } = useContactGroups();
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const { emailContacts, phoneContacts } = useContext(CustomerContactsContext);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    message: "",
    schedule: "",
    campaign_type: "feedback",
  });
  const [loading, setLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectionType, setSelectionType] = useState("group");
  const contacts = campaignType === "email" ? emailContacts : phoneContacts;

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGroupSelect = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      let contactIds = [];

      if (selectionType === "group") {
        if (selectedGroupIds.length === 0) {
          toast.error("Please select at least one group.");
          setLoading(false);
          return;
        }

        const allContacts = [];
        for (const groupId of selectedGroupIds) {
          const res = await listGroupContacts(groupId);
          const groupContacts = Array.isArray(res) ? res : res?.results || [];
          allContacts.push(...groupContacts);
        }
        contactIds = [...new Set(allContacts.map((c) => c.id))];

        if (contactIds.length === 0) {
          toast.error("No contacts found in the selected groups.");
          setLoading(false);
          return;
        }
      } else if (selectionType === "contact") {
        if (selectedContacts.length === 0) {
          toast.error("Please select at least one contact.");
          setLoading(false);
          return;
        }
        contactIds = selectedContacts.map((c) => c.id);
      }

      const campaignData = {
        name: formData.name,
        message_type: campaignType,
        campaign_type: formData.campaign_type,
        subject: formData.subject,
        message: formData.message,
        scheduled_at: new Date(formData.schedule).toISOString(),
        contact_ids: contactIds,
      };

      const token = localStorage.getItem("token");

      const response = await fetch(
        "https://zola.technology/api/company/campaigns/add/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(campaignData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result.non_field_errors?.[0] ||
          result.message ||
          "Failed to create campaign";
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success("Campaign created successfully!");
      refetchCampaigns();
      onClose();
    } catch (error) {
      console.error("Error creating campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
      <ToastContainer />
      <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg relative w-[750px]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-semibold mb-6">Create Campaign</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Message Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message Type
            </label>
            <p className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white">
              {campaignType === "sms" ? "SMS" : "Email"}
            </p>
          </div>

          {/* Campaign Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Campaign Type
            </label>
            <select
              name="campaign_type"
              value={formData.campaign_type}
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
              <option value="feedback">Feedback</option>
              <option value="survey">Survey</option>
            </select>
          </div>

          {/* Group/contact Selector Trigger */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setSelectionType("group")}
              className={`px-4 py-2 rounded-md text-sm ${
                selectionType === "group"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white"
              }`}
            >
              Groups
            </button>
            <button
              type="button"
              onClick={() => setSelectionType("contact")}
              className={`px-4 py-2 rounded-md text-sm ${
                selectionType === "contact"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white"
              }`}
            >
              Contacts
            </button>
          </div>

          {/* Group Picker Modal */}
          {selectionType === "group" ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Groups
                </label>
                <button
                  type="button"
                  onClick={() => setShowGroupModal(true)}
                  className="mt-1 w-full p-2 border rounded-md bg-white dark:bg-gray-800 dark:text-white text-left"
                >
                  {selectedGroupIds.length === 0
                    ? "Click to select groups"
                    : `${selectedGroupIds.length} group(s) selected`}
                </button>
              </div>

              {/* Group Picker Modal */}
              <GroupSelectorModal
                open={showGroupModal}
                onClose={() => setShowGroupModal(false)}
                groups={groups}
                selectedGroupIds={selectedGroupIds}
                setSelectedGroupIds={setSelectedGroupIds}
              />
            </>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Contacts
                </label>
                <button
                  type="button"
                  onClick={() => setShowContactModal(true)}
                  className="mt-1 w-full p-2 border rounded-md bg-white dark:bg-gray-800 dark:text-white text-left"
                >
                  {selectedContacts.length === 0
                    ? "Click to select contacts"
                    : `${selectedContacts.length} contact(s) selected`}
                </button>
              </div>

              {/* Contact Picker Modal */}
              <ContactSelectorModal
                open={showContactModal}
                onClose={() => setShowContactModal(false)}
                contacts={contacts}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
                campaignType={campaignType}
              />
            </>
          )}

          {/* Name and Subject */}
          <div className="flex gap-4">
            <div className={campaignType === "sms" ? "w-full" : "w-1/2"}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter campaign name"
                className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>

            {campaignType !== "sms" && (
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Subject
                </label>
                <input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Enter subject"
                  className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Enter your message"
              className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Schedule
            </label>
            <input
              type="datetime-local"
              name="schedule"
              value={formData.schedule}
              onChange={handleChange}
              required
              min={new Date().toISOString().slice(0, 16)}
              className="mt-1 w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>

          {/* Form Buttons */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              {loading ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
